import { Check, ChevronLeft, ChevronRight, Loader2, Pencil, X } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type RoadmapMonth, useSetRoadmapBudget } from "@/lib/api";
import { apiErrorCode, apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { shiftMonth } from "@/lib/calendar";
import { daysLabel } from "@/lib/effort";
import { formatMonth } from "@/lib/format";
import { cn } from "@/lib/utils";

import { MonthChart, StripeSwatch } from "./month-chart";
import { monthChart } from "./month-geometry";
import { StepButtons } from "./step-buttons";
import { leftFigure, monthBudget, type MonthKind, monthProgress, parseWholeHours } from "./utils";

interface MonthBlockProps {
  /**
   * The month's items, as the server counted them; for an owner and a
   * developer with its figures, its budget and what is left of it.
   */
  month: RoadmapMonth;
  /**
   * The month on show ("2026-12"): its name heads the block and ‹ › step
   * from it. While it loads, `month` still holds the figures of the one before.
   */
  shownMonth: string;
  /** Where `month` sits against the current month: a past month's empty bar says nothing was done. */
  kind: MonthKind;
  /**
   * Whether the viewer may change this month's budget (`budgetEdit`), and
   * whether the development team then hears of it; null when they may not.
   */
  budgetEdit: { notifiesTeam: boolean } | null;
  /** Steps to another month ("2026-12"). */
  onMonthChange: (month: string) => void;
  /** While the next month loads, the figures on show dim. */
  loading?: boolean;
  /** Opens an item's details, from its segment of the month's bar. */
  onOpenItem?: (id: string) => void;
}

/**
 * The month's work, with ‹ and › around its name to step months.
 *
 * For an owner and a developer, whose month carries its budget, it is the
 * work against the budget: four figures in days, Budget, Afgewerkt, Nog
 * gepland and Over (or Boven budget, next to the stripes the bar draws past
 * the budget, never in an alarming colour), and the month's items as one bar
 * against the budget (`<MonthChart>`). The budget changes in place, in
 * hours, for whoever `budgetEdit` lets, after a confirmation.
 *
 * For an admin, whose month carries no budget and no hours, it is the
 * month's progress alone: no figures, and the bar of the month's items, each
 * as wide as its share of the month. Every segment of the bar opens its item.
 */
export function MonthBlock({
  month,
  shownMonth,
  kind,
  budgetEdit,
  onMonthChange,
  loading = false,
  onOpenItem,
}: MonthBlockProps) {
  const { t } = useTranslation();
  // The month whose budget is being changed: stepping to another month closes the editor.
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  // When the editor closes, the focus goes back to the edit button, which the editor replaced.
  const refocus = useRef(false);
  const editButton = useRef<HTMLButtonElement>(null);
  const budget = monthBudget(month);
  const editing = budget !== null && budgetEdit !== null && editingMonth === month.month;
  const left = budget && leftFigure(budget.leftHours);
  // What the month's items used and still use, in hours; absent for an admin, who sees no hours.
  const progress = monthProgress(month);

  useEffect(() => {
    if (refocus.current && !editing) {
      refocus.current = false;
      editButton.current?.focus();
    }
  }, [editing]);

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6" aria-busy={loading || undefined}>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 pointer-coarse:size-10"
            aria-label={t("roadmap.month.previous")}
            onClick={() => onMonthChange(shiftMonth(shownMonth, -1))}
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
          </Button>
          <h2
            aria-live="polite"
            className="font-heading text-primary min-w-[9rem] text-center text-base font-bold first-letter:uppercase"
          >
            {formatMonth(shownMonth)}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 pointer-coarse:size-10"
            aria-label={t("roadmap.month.next")}
            onClick={() => onMonthChange(shiftMonth(shownMonth, 1))}
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </Button>
          {loading && (
            <Loader2
              className="text-muted ml-1 size-4 animate-spin"
              aria-label={t("common.loading")}
            />
          )}
        </div>
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60")}>
          {(budget || progress) && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              {budget && (
                <FigureItem
                  label={t("roadmap.month.budget")}
                  aside={
                    budgetEdit &&
                    !editing && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            ref={editButton}
                            variant="ghost"
                            size="icon"
                            className="size-6 pointer-coarse:size-10"
                            aria-label={t("roadmap.month.editBudget")}
                            onClick={() => setEditingMonth(month.month)}
                          >
                            <Pencil aria-hidden="true" className="size-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("roadmap.month.editBudget")}</TooltipContent>
                      </Tooltip>
                    )
                  }
                >
                  {editing ? (
                    <BudgetEditor
                      // A new editor per month, so it starts from that month's budget.
                      key={month.month}
                      month={month.month}
                      budgetHours={budget.budgetHours}
                      notifiesTeam={budgetEdit.notifiesTeam}
                      onDone={() => {
                        refocus.current = true;
                        setEditingMonth(null);
                      }}
                    />
                  ) : (
                    <Figure>{daysLabel(budget.budgetHours)}</Figure>
                  )}
                </FigureItem>
              )}
              {progress && (
                <>
                  <FigureItem label={t("roadmap.month.done")}>
                    <Figure>{daysLabel(progress.doneHours)}</Figure>
                  </FigureItem>
                  <FigureItem label={t("roadmap.month.planned")}>
                    <Figure>{daysLabel(progress.plannedHours)}</Figure>
                  </FigureItem>
                </>
              )}
              {budget && left && (
                <FigureItem label={t(`roadmap.month.${left.label}`)}>
                  {left.label === "over" ? (
                    // The stripes the bar draws past the budget, so the figure reads as that part.
                    <span className="flex items-center gap-2" data-figure="over">
                      <span className="flex gap-0.5">
                        {monthChart(budget.budgetHours, month.items).overTones.map((tone) => (
                          <StripeSwatch key={tone} tone={tone} className="size-4" />
                        ))}
                      </span>
                      <Figure>{daysLabel(left.hours)}</Figure>
                    </span>
                  ) : (
                    <Figure>{daysLabel(left.hours)}</Figure>
                  )}
                </FigureItem>
              )}
            </dl>
          )}
          <MonthChart
            month={month.month}
            budgetHours={budget?.budgetHours ?? null}
            items={month.items}
            onOpenItem={onOpenItem}
          />
          {month.items.length === 0 && (
            <p className="text-muted text-sm">
              {t(kind === "past" ? "roadmap.month.emptyPast" : "roadmap.month.empty", {
                month: formatMonth(month.month),
              })}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface FigureItemProps {
  label: string;
  /** Next to the label, such as the budget's edit button. */
  aside?: ReactNode;
  children: ReactNode;
}

/** One of the month's figures: its label, small and muted, over its value. */
function FigureItem({ label, aside, children }: FigureItemProps) {
  return (
    <div>
      <dt className="text-muted flex h-6 items-center gap-1 text-xs">
        {label}
        {aside}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

/** One of the month's figures, large, in the dashboard's blue whatever it says. */
function Figure({ children }: { children: string }) {
  return (
    <span className="text-primary text-xl font-bold tabular-nums sm:text-2xl">{children}</span>
  );
}

interface BudgetEditorProps {
  /** "2026-11". */
  month: string;
  budgetHours: number;
  /** An owner's change: the confirmation says the development team hears of it. */
  notifiesTeam: boolean;
  /** After a saved change, or on cancel. */
  onDone: () => void;
}

/**
 * The month's budget in whole hours, in place of the figure: a number box,
 * save and cancel. Enter saves, Escape cancels. A new budget is asked to be
 * confirmed first, in a dialog (`<BudgetConfirmPanel>`) that says the change
 * in days; a failed save says why there.
 */
function BudgetEditor({ month, budgetHours, notifiesTeam, onDone }: BudgetEditorProps) {
  const { t } = useTranslation();
  const hintId = useId();
  const descriptionId = useId();
  const save = useSetRoadmapBudget();
  const [text, setText] = useState(String(budgetHours));
  // The budget waiting for its confirmation; null while the dialog is shut.
  const [confirming, setConfirming] = useState<number | null>(null);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const saved = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const hours = parseWholeHours(text);
  const valid = hours !== undefined && hours !== null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid || save.isPending) {
      return;
    }
    if (hours === budgetHours) {
      onDone();
      return;
    }
    setFailure(null);
    setConfirming(hours);
  };

  const confirm = async () => {
    if (confirming === null || save.isPending) {
      return;
    }
    setFailure(null);
    try {
      await save.mutateAsync({ month, budgetHours: confirming });
      saved.current = true;
      setConfirming(null);
      onDone();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-1">
        <div className="flex items-center gap-1">
          <Input
            ref={input}
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                onDone();
              }
            }}
            aria-label={t("roadmap.month.budgetHours", { month: formatMonth(month) })}
            aria-invalid={!valid || undefined}
            aria-describedby={hintId}
            autoFocus
            className="h-8 w-20 tabular-nums pointer-coarse:h-10"
          />
          <Button
            type="submit"
            variant="ghost"
            size="icon"
            className="size-8 pointer-coarse:size-10"
            aria-label={t("common.save")}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Check aria-hidden="true" className="size-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 pointer-coarse:size-10"
            aria-label={t("common.cancel")}
            onClick={onDone}
          >
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
        <p id={hintId} className={cn("text-xs", valid ? "text-muted" : "text-destructive")}>
          {valid ? t("roadmap.month.budgetHint") : t("roadmap.month.budgetInvalid")}
        </p>
      </form>
      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open && !save.isPending) {
            setConfirming(null);
            setFailure(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          aria-describedby={descriptionId}
          // Opened without a trigger, the dialog would leave the focus on the page: back to the box
          // after a cancel. After a save the editor is gone, and the month block sends the focus to
          // its edit button.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!saved.current) {
              input.current?.focus();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("roadmap.month.editBudget")}</DialogTitle>
          </DialogHeader>
          {confirming !== null && (
            <BudgetConfirmPanel
              descriptionId={descriptionId}
              month={month}
              fromHours={budgetHours}
              toHours={confirming}
              notifiesTeam={notifiesTeam}
              pending={save.isPending}
              failure={failure}
              onCancel={() => {
                setConfirming(null);
                setFailure(null);
              }}
              onConfirm={() => void confirm()}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

interface BudgetConfirmPanelProps {
  /** The id of its sentence, which describes the dialog. */
  descriptionId?: string;
  /** "2026-11". */
  month: string;
  fromHours: number;
  toHours: number;
  /** An owner's change: the development team hears of it. */
  notifiesTeam: boolean;
  pending: boolean;
  /** A save that failed, worded here so a language switch re-words it. */
  failure: { error: unknown } | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The confirmation of a new budget, without the dialog around it (so it
 * renders on its own in tests): the month and its budget before and after,
 * in days, for an owner that the development team hears of it, a failed
 * save, and Annuleren and Bevestigen.
 */
export function BudgetConfirmPanel({
  descriptionId,
  month,
  fromHours,
  toHours,
  notifiesTeam,
  pending,
  failure,
  onCancel,
  onConfirm,
}: BudgetConfirmPanelProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-section="budget-confirm">
      <div id={descriptionId} className="space-y-2 text-sm">
        <p>
          {t("roadmap.month.confirmBody", {
            month: formatMonth(month),
            from: daysLabel(fromHours),
            to: daysLabel(toHours),
          })}
        </p>
        {notifiesTeam && <p className="text-muted">{t("roadmap.month.confirmNotify")}</p>}
      </div>
      {failure && (
        <p role="alert" className="text-destructive text-sm">
          {/* The server's refusal names an item; for a budget, say what was refused. */}
          {apiErrorCode(failure.error) === "roadmap_forbidden"
            ? t("roadmap.month.budgetForbidden")
            : apiErrorMessage(failure.error, "setRoadmapBudget")}
        </p>
      )}
      <StepButtons
        onBack={onCancel}
        onConfirm={onConfirm}
        confirm={t("roadmap.month.confirm")}
        pending={pending}
      />
    </div>
  );
}
